const ISO_COUNTRY = /^[A-Z]{2}$/;

/** Vercel sets `x-vercel-ip-country` to a 2-letter ISO code, or `XX` when unknown. */
export function normalizeBkCountry(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const country = value.trim().toUpperCase();
  if (!ISO_COUNTRY.test(country) || country === 'XX') return null;
  return country;
}

export function bkCountryProperties(
  country: string | null | undefined
): { bk_country: string } | null {
  const normalized = normalizeBkCountry(country);
  return normalized ? { bk_country: normalized } : null;
}

export const BK_COUNTRY_RESOLVE_TIMEOUT_MS = 1500;

export async function fetchBkCountryFromGeo(
  fetchImpl: typeof fetch = fetch
): Promise<string | null> {
  try {
    const response = await fetchImpl('/api/geo', { credentials: 'omit' });
    if (!response.ok) return null;
    const payload = (await response.json()) as { country?: unknown };
    return normalizeBkCountry(typeof payload?.country === 'string' ? payload.country : null);
  } catch {
    return null;
  }
}

/** Resolve country from a known header or `/api/geo`, timing out so capture is not blocked. */
export async function resolveBkCountryWithTimeout(
  country?: string | null,
  fetchImpl?: typeof fetch,
  timeoutMs: number = BK_COUNTRY_RESOLVE_TIMEOUT_MS
): Promise<{ bk_country: string } | null> {
  const fromKnown = bkCountryProperties(country);
  if (fromKnown) return fromKnown;

  const fetchFn = fetchImpl ?? (typeof fetch === 'function' ? fetch : undefined);
  if (!fetchFn) return null;

  return Promise.race([
    fetchBkCountryFromGeo(fetchFn).then((value) => bkCountryProperties(value)),
    new Promise<{ bk_country: string } | null>((resolve) => {
      setTimeout(() => resolve(null), timeoutMs);
    }),
  ]);
}
